import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem, Stack, TextField } from '@mui/material';
import { Formik } from 'formik';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import PublishIcon from '@mui/icons-material/Publish';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';

import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs } from 'src/components/organisms/ContentCard';
import { FormContainerMolecule } from 'src/components/molecules/formik/FormContainerMolecule';
import { FormField } from 'src/components/molecules/formik/FormField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import SelectTableDialog from 'src/components/organisms/tables/SelectTableDialog';

import FormHelper from 'src/utils/formHelper';
import useUpdateEffect from 'src/hooks/useUpdateEffect';

import { useCampaignForm } from 'src/hooks/forms/useForms';
import { useCampaignOptions } from 'src/hooks/forms/useOptions';
import { useFormDefaults } from 'src/hooks/forms/useFormDefaults';
import { FormDefaultsMenu } from 'src/components/molecules/formik/FormDefaultsMenu';

import useCampaignSubmit from 'src/hooks/forms/configs/useCampaignSubmit';
import { useCampaignSelectorTables, type CampaignBulkPriceEdit, type CampaignBulkPriceEditKind } from 'src/hooks/table/configs/useSelectItemsOnCampaign';
import useRedirectOnCreate from 'src/hooks/forms/useRedirectOnCreate';

type KV = { Key?: number | null; Value?: string | null } | null | undefined;

interface CampaignValues {
  Id?: number | null;
  Name?: string | null;
  SalesText?: string | null;
  CampaignType?: number | string | null;
  Status?: KV;
  ValidFrom?: string | Date | null;
  ValidTo?: string | Date | null;
  Status_Value: any;
  SalesPriceLists?: any[] | null;
  CampaignSalesItems?: any[] | null;
  CampaignPrices?: any[] | null;
  CampaignSalesRule?: {
    Id?: number | null;
    Prio?: number | null;
    CampaignId?: number | null;
    OrderDiscount?: number | null;
    OrderDiscountType?: KV;
    MinimumAmountGross?: number | null;
    MinimumQuantity?: number | null;
    FreeArticleSalesItemId?: number | null;
    FreeQuantity?: number | null;
    FreeArticleSalesItemName?: string | null;
  } | null;
}

const scheme = Yup.object().shape({
  Id: Yup.number().nullable(),
  Name: Yup.string().required('Name is required'),
  SalesText: Yup.string().nullable(),
  CampaignType: Yup.number().typeError('Select a campaign type so the campaign is handled correctly.').required('Select a campaign type so the campaign is handled correctly.'),
  Status: Yup.object({ Key: Yup.number().nullable(), Value: Yup.string().nullable() }).nullable(),
  ValidFrom: Yup.date().nullable(),
  ValidTo: Yup.date().nullable().min(Yup.ref('ValidFrom'), 'Valid To must be after Valid From'),
  SalesPriceLists: Yup.array().nullable(),
  CampaignSalesItems: Yup.array().nullable(),
  CampaignPrices: Yup.array().nullable(),
  CampaignSalesRule: Yup.mixed().nullable()
});

const defaultInitialValues = { Status_Value: '', CampaignType: '' };

const parseNumLoose = (value: any) => {
  if (value == null) return NaN;
  const cleaned = String(value).replace(',', '.').trim();
  if (cleaned === '') return NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const stripLeadingZeroIfMultiDigit = (value: string) => (/^0\d+$/.test(value) ? String(Number(value)) : value);

function CampaignPriceBulkEditDialog({
  open,
  selectedCount,
  onClose,
  onApply,
}: {
  open: boolean;
  selectedCount: number;
  onClose: () => void;
  onApply: (edit: CampaignBulkPriceEdit) => void;
}) {
  const [activeField, setActiveField] = useState<CampaignBulkPriceEditKind>('fixedPrice');
  const [fixedPriceInput, setFixedPriceInput] = useState('');
  const [discountAmountInput, setDiscountAmountInput] = useState('');
  const [discountRateInput, setDiscountRateInput] = useState('');

  useEffect(() => {
    if (!open) return;
    setActiveField('fixedPrice');
    setFixedPriceInput('');
    setDiscountAmountInput('');
    setDiscountRateInput('');
  }, [open]);

  const activeEdit = useMemo<CampaignBulkPriceEdit | null>(() => {
    const value = activeField === 'fixedPrice' ? parseNumLoose(fixedPriceInput) : activeField === 'discountAmount' ? parseNumLoose(discountAmountInput) : parseNumLoose(discountRateInput);
    return Number.isFinite(value) ? { kind: activeField, value } : null;
  }, [activeField, fixedPriceInput, discountAmountInput, discountRateInput]);

  const setExclusiveValue = (kind: CampaignBulkPriceEditKind, value: string) => {
    const nextInput = stripLeadingZeroIfMultiDigit(value ?? '');
    setActiveField(kind);
    setFixedPriceInput(kind === 'fixedPrice' ? nextInput : '');
    setDiscountAmountInput(kind === 'discountAmount' ? nextInput : '');
    setDiscountRateInput(kind === 'discountRate' ? nextInput : '');
  };

  const handleApply = () => {
    if (!activeEdit) return;
    onApply(activeEdit);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk edit campaign prices</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Fixed price"
              value={fixedPriceInput}
              onChange={(event) => setExclusiveValue('fixedPrice', event.target.value)}
              size="small"
              type="text"
              inputMode="decimal"
              fullWidth
              autoFocus
            />
            <TextField
              label="Discount amount"
              value={discountAmountInput}
              onChange={(event) => setExclusiveValue('discountAmount', event.target.value)}
              size="small"
              type="text"
              inputMode="decimal"
              fullWidth
            />
            <TextField
              label="Discount rate"
              value={discountRateInput}
              onChange={(event) => setExclusiveValue('discountRate', event.target.value)}
              size="small"
              type="text"
              inputMode="decimal"
              fullWidth
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </Stack>
          <span>{selectedCount} selected campaign price rows will be updated.</span>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained" disabled={!activeEdit}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}


const getCampaignSelectorState = (statusValue: any) => {
  const normalizedStatus = String(statusValue ?? '').trim().toLowerCase();
  const selectorEnabled = normalizedStatus !== 'closed';
  return { selectorEnabled, selectorTooltipText: selectorEnabled ? '' : 'Sales items and price lists cannot be changed when the campaign is Closed.' };
};

export const EditCampaign = () => {
  const [editId, PageName, , , isEditPage] = FormHelper.getEditPageConfig('Campaign');
  const initialValues: CampaignValues | any = useCampaignForm(editId, defaultInitialValues);
  const [results, submit, closeCampaignSubmit, publishCampaignSubmit] = useCampaignSubmit(isEditPage, null);
  const [campaignTypeOptions, , , discountTypeOptions, rawSalesPriceLists] = useCampaignOptions();

  const [
    salesItemDisplayColumns,
    salesItemDisplayTable,
    priceListDisplayColumns,
    priceListDisplayTable,
    campaignPricesColumns,
    campaignPricesTable,
    campaignTableData,
    campaignPriceEditModes,
    campaignPriceBulkEditDialog,
    salesItemSelector,
    priceListSelector,
  ] = useCampaignSelectorTables(initialValues, rawSalesPriceLists ?? [], discountTypeOptions ?? []);

  const campaignSubmitData = useMemo(() => ({ ...campaignTableData, campaignPriceEditModes }), [campaignTableData, campaignPriceEditModes]);

  const [submitting, setSubmitting] = useState(false);
  useUpdateEffect(() => { setSubmitting(false); }, [results]);

  const { FormDefaultsListener, formDefaultsProps } = useFormDefaults({ formKey: 'EditCampaignForm', isEditPage, fields: { CampaignType: {}, ValidFrom: {}, ValidTo: {} } });

  const hasSalesPriceLists = (initialValues?.SalesPriceLists?.length ?? 0) > 0;
  const hasSalesItems = (initialValues?.CampaignSalesItems?.length ?? 0) > 0;
  const hasPrices = (initialValues?.CampaignPrices?.length ?? 0) > 0;
  const hasRule = !!initialValues?.CampaignSalesRule && Object.keys(initialValues?.CampaignSalesRule ?? {}).length > 0;

  const tabs: CardTab[] = useMemo<CardTab[]>(() => [
    { value: 'Campaign Prices', label: 'Campaign Prices' },
    { value: 'Campaign Sales Items', label: 'Campaign Sales Items' },
    
    { value: 'Sales Price Lists', label: 'Sales Price Lists' },
    { value: 'Campaign Rule', label: 'Campaign Rule' }
  ], []);

  const computeDefaultTab = () => {
    if (hasPrices) return 'Campaign Prices';
    if (hasSalesItems) return 'Campaign Sales Items';
    
    if (hasSalesPriceLists) return 'Sales Price Lists';
    if (hasRule) return 'Campaign Rule';
    return tabs[0].value;
  };

  const [currentTab, setCurrentTab] = useState<string>();
  useEffect(() => {
    setCurrentTab(prev => {
      const defaultTab = computeDefaultTab();
      if (!prev) return defaultTab;
      const stillExists = tabs.some(t => t.value === prev);
      return stillExists ? prev : defaultTab;
    });
  }, [hasSalesItems, hasPrices, hasSalesPriceLists, hasRule, tabs]);

  useRedirectOnCreate(results?.mainResult, editId, 'Id', '/orders/campaigns/edit?id=');
  return (
    <>
      <Formik
        initialValues={initialValues}
        validateOnBlur={true}
        validateOnChange={false}
        validationSchema={scheme}
        onSubmit={(values: any, formikHelpers: any) => {
          setSubmitting(true);
          submit(values, campaignSubmitData, formikHelpers);
        }}
        enableReinitialize
      >
        {(props): JSX.Element => {
          const { selectorEnabled, selectorTooltipText } = getCampaignSelectorState(props.values?.Status_Value);
          const campaignStatus = String(props.values?.Status_Value ?? initialValues?.Status_Value ?? '').trim().toLowerCase();
          const isDraftCampaign = campaignStatus === 'draft';
          const isClosedCampaign = campaignStatus === 'closed';

          return (
            <div>
              <FormDefaultsListener formikProps={props} />
              {selectorEnabled && <SelectTableDialog {...salesItemSelector} />}
              {selectorEnabled && <SelectTableDialog {...priceListSelector} />}
              <CampaignPriceBulkEditDialog {...campaignPriceBulkEditDialog} />

              <PageHeader
                {...{
                  title: PageName,
                  initialValues,
                  subTitle: 'Edit and manage Campaign details',
                  components: [
                    {
                      component: (
                        <CustomButton
                          disabled={!isEditPage || !isDraftCampaign}
                          tooltipText={!isEditPage ? 'Campaign must be created before it can be published.' : !isDraftCampaign ? 'Only campaigns with Draft status can be published.' : undefined}
                          isSubmitting={submitting}
                          onClick={() => { setSubmitting(true); publishCampaignSubmit(String(editId), props); }}
                          title="Publish"
                          icon={<PublishIcon />}
                        />
                      )
                    },
                    {
                      component: (
                        <CustomButton
                          disabled={!isEditPage || isClosedCampaign}
                          tooltipText={!isEditPage ? 'Campaign must be created before it can be closed.' : isClosedCampaign ? 'Campaign is already closed.' : undefined}
                          isSubmitting={submitting}
                          onClick={() => { setSubmitting(true); closeCampaignSubmit(String(editId), props); }}
                          title="Close"
                          icon={<LockIcon />}
                        />
                      )
                    },
                    { isInGroupedMenu: true, component: <FormDefaultsMenu formikProps={props} formDefaultsProps={formDefaultsProps} /> },
                    { isInGroupedMenu: false, component: <CustomButton isSubmitting={submitting} onClick={props.handleSubmit} title="Save" icon={<SaveIcon />} /> }
                  ],
                  alertData: { dataArray: results ? Object.values(results).filter(Boolean) : null }
                }}
              />

              <ContentCardsContainer>
                <ContentCard {...{ cardId: 'Campaign Info', md: 12 }}>
                  <FormContainerMolecule formikProps={props}>
                    <Grid container spacing={2}>
                      <FormField fieldProps={{ name: 'Name', label: 'Name', md: 11.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'Status_Value', label: 'Status', md: 5.8, disabled: true }} formikProps={props} />
                      <FormField fieldProps={{ name: 'CampaignType', select: true, label: 'Campaign Type', md: 5.8, tooltipText: 'Select what kind of campaign this is, for example price reduction, free shipping or order discount.' }} formikProps={props}>
                        {campaignTypeOptions?.map((option: any) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                      </FormField>
                      <FormField fieldProps={{ name: 'ValidFrom', label: 'Valid From', type: 'date', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'ValidTo', label: 'Valid To', type: 'date', md: 3.8 }} formikProps={props} />
                      <FormField fieldProps={{ name: 'SalesText', label: 'Sales Text', multiline: true, rows: 3, md: 11.8 }} formikProps={props} />
                    </Grid>
                  </FormContainerMolecule>
                </ContentCard>
              </ContentCardsContainer>

              <ContentCardsContainer>
                <ContentCard
                  {...{
                    cardId: 'TabContainer',
                    contentPadding: 0,
                    cardTypes: [CardType.tabContainer],
                    cardHeader: <HeaderTabs {...{ tabs, currentTab: currentTab ?? computeDefaultTab(), setCurrentTab }} />,
                    components: [
                      { component: <CustomButton onClick={() => salesItemSelector.openSelector()} title="Add Sales Item" icon={<AddTwoToneIcon />} disabled={!selectorEnabled} tooltipText={selectorTooltipText} /> },
                      { component: <CustomButton onClick={() => priceListSelector.openSelector()} title="Add Price List" icon={<AddTwoToneIcon />} disabled={!selectorEnabled} tooltipText={selectorTooltipText} /> },
                    ]
                  }}
                >
                  {(currentTab ?? computeDefaultTab()) === 'Campaign Prices' && (
                    <ContentCard {...{ cardId: 'Campaign Prices', cardTypes: [CardType.tab], contentPadding: 0 }}>
                      <CustomDataGrid columns={campaignPricesColumns} tableSettings={campaignPricesTable} />
                    </ContentCard>
                  )}

                  {(currentTab ?? computeDefaultTab()) === 'Campaign Sales Items' && (
                    <ContentCard {...{ cardId: 'Campaign Sales Items', cardTypes: [CardType.tab], contentPadding: 0 }}>
                      <CustomDataGrid columns={salesItemDisplayColumns} tableSettings={salesItemDisplayTable} />
                    </ContentCard>
                  )}

                  {(currentTab ?? computeDefaultTab()) === 'Sales Price Lists' && (
                    <ContentCard {...{ cardId: 'Sales Price Lists', cardTypes: [CardType.tab], contentPadding: 0 }}>
                      <CustomDataGrid columns={priceListDisplayColumns} tableSettings={priceListDisplayTable} />
                    </ContentCard>
                  )}

                  {(currentTab ?? computeDefaultTab()) === 'Campaign Rule' && (
                    <ContentCard {...{ cardId: 'Campaign Rule', cardTypes: [CardType.tab], contentPadding: 2 }}>
                      {initialValues?.CampaignSalesRule ? (
                        <FormContainerMolecule formikProps={props}>
                          <Grid container spacing={2}>
                            <FormField fieldProps={{ name: 'CampaignSalesRule_Id', label: 'Rule ID', md: 3.8, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_CampaignId', label: 'Campaign ID', md: 3.8, disabled: true }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_Prio', label: 'Priority', type: 'number', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_OrderDiscount', label: 'Order Discount', type: 'number', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_OrderDiscountType_Value', label: 'Discount Type', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_MinimumAmountGross', label: 'Min Order Amount (gross)', type: 'number', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_MinimumQuantity', label: 'Min Quantity', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_FreeArticleSalesItemId', label: 'Free Article ID', type: 'number', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_FreeQuantity', label: 'Free Quantity', type: 'number', md: 3.8 }} formikProps={props} />
                            <FormField fieldProps={{ name: 'CampaignSalesRule_FreeArticleSalesItemName', label: 'Free Article Name', md: 7.8 }} formikProps={props} />
                          </Grid>
                        </FormContainerMolecule>
                      ) : (
                        <div style={{ padding: 16 }}>No campaign rule defined.</div>
                      )}
                    </ContentCard>
                  )}
                </ContentCard>
              </ContentCardsContainer>
            </div>
          );
        }}
      </Formik>

      <Footer />
    </>
  );
};

export default EditCampaign;